/**
 * Dynamic Schema Service
 *
 * Service for fetching dynamic schemas from connector APIs.
 * Supports x-ms-dynamic-schema pattern for generating additional
 * parameters based on selected values (e.g., SharePoint list columns).
 *
 * Uses the same API patterns as connectorDynamicService.ts.
 */

import type { ConnectionReference } from '@/types/connectionReference';
import type { DynamicSchemaConfig, DynamicValuesConfig, ParameterInfo } from '@/types/operationParameter';
import {
  ParameterType,
  ParameterEditor,
  ParameterVisibility,
  DynamicLoadStatus,
} from '@/types/operationParameter';
import type { SchemaObject } from '@/types/valueSegment';
import type { SwaggerParser, ParsedOperation } from '@/parsers/SwaggerParser';
import { buildDynamicInvokeUrl, buildResolvedServiceProviderConnectionPayload } from './serviceProviderRuntime';
import { postManagedApiDynamicInvoke } from './dynamicConnectionService';
import { extractServiceProviderName, isClientBuiltInConnector, isServiceProviderConnector } from '@/utils/connections';
import { schemaTypeToParameterType } from '@/utils/schema';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Request parameters for fetching dynamic schema
 */
export interface FetchDynamicSchemaRequest {
  /**
   * Connection reference for the connector. Optional — runtime literal-type
   * built-ins (e.g. ChunkText under `connectionProviders/aiOperations`) invoke
   * `dynamicInvoke` directly via the runtime path with no connection envelope.
   */
  connectionReference?: ConnectionReference | undefined;
  /** Connector/API ID */
  connectorId: string;
  /** Dynamic schema configuration from the parameter */
  schemaConfig: DynamicSchemaConfig;
  /** Resolved parameter values (dependencies) */
  resolvedParameters: Record<string, unknown>;
  /** Azure access token */
  accessToken: string;
  /**
   * Swagger parser for the connector.
   * Required for legacy x-ms-dynamic-schema to look up the target operation
   * and build the proper /dynamicInvoke request structure.
   */
  swagger?: SwaggerParser | undefined;
  /** MI token audience (from `useMiAudience`). Required only when `isManagedIdentity` is true. */
  miAudience?: string;
  /** Whether the connection authenticates via MI (from `useManagedConnectionInfo`). */
  isManagedIdentity?: boolean;
  /** Live `connectionRuntimeUrl` from the connection ARM resource (from `useManagedConnectionInfo`). */
  connectionRuntimeUrl?: string;
}

/**
 * Response from dynamic schema API
 */
export interface FetchDynamicSchemaResponse {
  /** Fetched schema */
  schema: SchemaObject | null;
  /** Error if fetch failed */
  error?: string;
}

/**
 * Raw response from Azure dynamic schema APIs
 */
interface DynamicSchemaApiResponse {
  body?: unknown;
  statusCode?: number;
  headers?: Record<string, string>;
}

// =============================================================================
// CONSTANTS
// =============================================================================


// Cache for dynamic schema results (5 minute TTL)
const dynamicSchemaCache = new Map<string, { schema: SchemaObject | null; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

// =============================================================================
// MAIN FETCH FUNCTION
// =============================================================================

/**
 * Fetch dynamic schema from connector API
 *
 * Supports two patterns:
 * 1. API Connections (managed connectors): POST {connectionId}/dynamicInvoke
 * 2. Service Providers: POST /runtime/webhooks/workflow/api/management/operationGroups/{connectorId}/operations/{operationId}/dynamicInvoke
 */
export async function fetchDynamicSchema(
  request: FetchDynamicSchemaRequest
): Promise<FetchDynamicSchemaResponse> {
  const { connectionReference, connectorId, schemaConfig, resolvedParameters, accessToken, swagger, miAudience, isManagedIdentity, connectionRuntimeUrl } = request;
  const connectionId = connectionReference?.connection.id;
  // Validate access token before making API calls
  if (!isValidAccessToken(accessToken)) {
    return { schema: null, error: 'Invalid or missing access token' };
  }

  // Check cache first. Cache scope falls back to connectorId when there's no
  // connection (runtime built-ins) — those are addressed purely by group/op.
  const cacheKey = buildCacheKey(connectionId ?? connectorId, schemaConfig.operationId, resolvedParameters, {
    isManagedIdentity,
    miAudience,
    connectionRuntimeUrl,
  });
  const cached = dynamicSchemaCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return { schema: cached.schema };
  }

  try {
    // Determine connector type and call appropriate API.
    // Both `/serviceProviders/{name}` and `connectionProviders/{name}` (built-in
    // runtime ops like ChunkText) hit the same `operationGroups/.../dynamicInvoke`
    // runtime endpoint; only the connection envelope differs.
    const useRuntimePath = isServiceProviderConnector(connectorId) || isClientBuiltInConnector(connectorId);

    let response: DynamicSchemaApiResponse;
    if (useRuntimePath) {
      response = await callServiceProviderDynamicApi(
        connectorId,
        schemaConfig,
        resolvedParameters,
        connectionReference,
        accessToken
      );
    } else {
      if (!connectionId) {
        return { schema: null, error: 'Managed API dynamic schema requires a connection reference' };
      }
      response = await callManagedApiDynamicSchema(
        connectionReference!,
        schemaConfig,
        resolvedParameters,
        accessToken,
        swagger,
        miAudience,
        isManagedIdentity,
        connectionRuntimeUrl
      );
    }

    // Parse response into schema
    const schema = parseResponseToSchema(response, schemaConfig);

    // Cache the result
    dynamicSchemaCache.set(cacheKey, { schema, timestamp: Date.now() });

    return { schema };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch dynamic schema';
    console.error('Dynamic schema fetch error:', error);
    return { schema: null, error: message };
  }
}

// =============================================================================
// API CALL IMPLEMENTATIONS
// =============================================================================

/**
 * Build the request structure for legacy x-ms-dynamic-schema API call.
 *
 * For legacy dynamic schema (x-ms-dynamic-schema), we need to:
 * 1. Look up the target operation in swagger by operationId
 * 2. Build an HTTP request structure with parameters in correct locations
 * 3. Call /dynamicInvoke with { request: { method, path, body, queries, headers } }
 *
 * This follows the LogicAppsUX pattern in buildOperationDetailsFromControls().
 */
function buildLegacyDynamicSchemaRequest(
  operation: ParsedOperation,
  resolvedParameters: Record<string, unknown>
): { method: string; path: string; body?: unknown; queries?: Record<string, unknown>; headers?: Record<string, unknown> } {
  const { method, path } = operation;

  // Build the request structure by placing parameters in their correct locations
  const bodyParams: Record<string, unknown> = {};
  const queryParams: Record<string, unknown> = {};
  const headerParams: Record<string, unknown> = {};
  let pathWithParams = path;

  // Remove {connectionId} placeholder - matches LogicAppsUX's removeConnectionPrefix()
  // The API handles connection routing via the URL, not the path
  pathWithParams = pathWithParams.replace('{connectionId}', '');
  pathWithParams = pathWithParams.replace('//', '/'); // Fix any resulting double slashes

  // Get parameter definitions from the target operation to know where each param goes.
  // Also capture x-ms-url-encoding so SharePoint-style "double"-encoded path params
  // (e.g. dataset = site URL) round-trip correctly. Mirrors LogicAppsUX's
  // `getEncodeValue` / `encodePathValue` (helper.ts in libs/designer).
  const opParams = operation.operation.parameters ?? [];
  const paramLocations = new Map<string, string>();
  const paramEncodeCounts = new Map<string, number>();

  for (const param of opParams) {
    const key = param.name.toLowerCase();
    paramLocations.set(key, param.in);
    const encoding = (param as { 'x-ms-url-encoding'?: unknown })['x-ms-url-encoding'];
    paramEncodeCounts.set(key, typeof encoding === 'string' && encoding.toLowerCase() === 'double' ? 2 : 1);
  }

  // Map resolved parameters to their correct locations
  for (const [key, value] of Object.entries(resolvedParameters)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Find where this parameter should go
    const lowerKey = key.toLowerCase();
    const location = paramLocations.get(lowerKey);

    switch (location) {
      case 'path': {
        const encodeCount = paramEncodeCounts.get(lowerKey) ?? 1;
        let encoded = String(value);
        for (let i = 0; i < encodeCount; i++) {
          encoded = encodeURIComponent(encoded);
        }
        // Replace path parameter template (case-insensitive fallback)
        if (pathWithParams.includes(`{${key}}`)) {
          pathWithParams = pathWithParams.replace(`{${key}}`, encoded);
        } else {
          pathWithParams = pathWithParams.replace(new RegExp(`\\{${key}\\}`, 'i'), encoded);
        }
        break;
      }
      case 'query':
        queryParams[key] = value;
        break;
      case 'header':
        headerParams[key] = value;
        break;
      case 'body':
        // Body parameters typically go into request body
        bodyParams[key] = value;
        break;
      default:
        // If location unknown, default to body for object params, query for primitives
        if (typeof value === 'object') {
          bodyParams[key] = value;
        } else {
          queryParams[key] = value;
        }
    }
  }

  // Build the request object
  const request: {
    method: string;
    path: string;
    body?: unknown;
    queries?: Record<string, unknown>;
    headers?: Record<string, unknown>;
  } = {
    method: method.toUpperCase(),
    path: pathWithParams,
  };

  // Only include non-empty objects
  if (Object.keys(bodyParams).length > 0) {
    request.body = bodyParams;
  }
  if (Object.keys(queryParams).length > 0) {
    request.queries = queryParams;
  }
  if (Object.keys(headerParams).length > 0) {
    request.headers = headerParams;
  }

  return request;
}

/**
 * Call managed API connection's dynamicInvoke endpoint for schema fetching.
 *
 * URL/body routing (MI vs legacy) and the actual POST are handled in
 * {@link postManagedApiDynamicInvoke}; this function only resolves the
 * swagger operation and shapes the `request` envelope.
 */
async function callManagedApiDynamicSchema(
  connectionReference: ConnectionReference,
  schemaConfig: DynamicSchemaConfig,
  resolvedParameters: Record<string, unknown>,
  accessToken: string,
  swagger?: SwaggerParser,
  miAudience?: string,
  isManagedIdentity?: boolean,
  connectionRuntimeUrl?: string
): Promise<DynamicSchemaApiResponse> {
  if (!swagger) {
    throw new Error('Swagger is required for dynamic schema fetching with legacy x-ms-dynamic-schema');
  }

  const operation = swagger.getOperationByOperationId(schemaConfig.operationId);
  if (!operation) {
    throw new Error(`Operation '${schemaConfig.operationId}' not found in swagger`);
  }

  return postManagedApiDynamicInvoke<DynamicSchemaApiResponse>({
    connectionReference,
    request: buildLegacyDynamicSchemaRequest(operation, resolvedParameters),
    accessToken,
    miAudience,
    isManagedIdentity,
    connectionRuntimeUrl,
    label: 'schema',
  });
}

/**
 * Call service provider connector's dynamicInvoke endpoint
 *
 * Endpoint: POST {runtimeUrl}/runtime/webhooks/workflow/api/management/operationGroups/{groupName}/operations/{operationId}/dynamicInvoke
 *
 * Handles both `/serviceProviders/{name}` (legacy SP) and
 * `connectionProviders/{name}` (built-in runtime ops, e.g. aiOperations). The
 * runtime endpoint is identical; only the connection envelope differs — runtime
 * built-ins typically have no connection.
 */
async function callServiceProviderDynamicApi(
  connectorId: string,
  schemaConfig: DynamicSchemaConfig,
  resolvedParameters: Record<string, unknown>,
  connectionReference: ConnectionReference | undefined,
  accessToken: string
): Promise<DynamicSchemaApiResponse> {
  // Extract the group name. For `/serviceProviders/{name}` extractServiceProviderName
  // already does the right thing; for `connectionProviders/{name}` we read the slug
  // after the marker directly. Both forms route to the same runtime URL shape.
  const providerName = extractRuntimeGroupName(connectorId);

  const url = buildDynamicInvokeUrl(providerName, schemaConfig.operationId);

  // For runtime built-ins without a connection (e.g. ChunkText), omit the
  // `configuration.connection` block entirely — the backend payload contract
  // (`ServiceProviderDynamicInvokeInputDefinition`) makes Configuration optional.
  // See connectorDynamicService.callServiceProviderDynamicApi for payload shape rationale.
  const body: Record<string, unknown> = { parameters: resolvedParameters };
  if (connectionReference) {
    const connectionPayload = await buildResolvedServiceProviderConnectionPayload(
      connectorId,
      connectionReference,
      accessToken,
    );
    body.configuration = { connection: connectionPayload };
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Service provider dynamic API failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

/**
 * Resolve the runtime operation-group slug from a connector id, handling both
 * `/serviceProviders/{name}` (legacy SP) and `connectionProviders/{name}`
 * (built-in runtime ops). Case-insensitive — returns the original-cased slug.
 */
function extractRuntimeGroupName(connectorId: string): string {
  const spMatch = connectorId.match(/\/serviceProviders\/([^/]+)/i);
  if (spMatch?.[1]) return spMatch[1];
  const cpMatch = connectorId.match(/(?:^|\/)connectionProviders\/([^/]+)/i);
  if (cpMatch?.[1]) return cpMatch[1];
  // Fallback to legacy extractor (returns connectorId unchanged on miss).
  return extractServiceProviderName(connectorId);
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

/**
 * Parse API response into SchemaObject
 */
function parseResponseToSchema(
  response: DynamicSchemaApiResponse | null | undefined,
  config: DynamicSchemaConfig
): SchemaObject | null {
  // Validate response exists
  if (!response) {
    console.warn('[parseResponseToSchema] Response is null or undefined');
    return null;
  }

  // Handle dynamicInvoke response format: { response: { statusCode, body } }
  // This is different from dynamicList which returns directly
  let data: unknown;

  // Check for dynamicInvoke nested response structure
  const dynamicInvokeResponse = (response as { response?: { statusCode?: string; body?: unknown } }).response;
  if (dynamicInvokeResponse) {
    if (dynamicInvokeResponse.statusCode !== 'OK') {
      console.warn('[parseResponseToSchema] dynamicInvoke returned non-OK status:', dynamicInvokeResponse.statusCode);
      return null;
    }
    data = dynamicInvokeResponse.body;
  } else {
    // Direct response format
    data = response.body ?? response;
  }

  // Validate data exists before processing
  if (data === null || data === undefined) {
    console.warn('[parseResponseToSchema] Response body is null or undefined');
    return null;
  }

  // Navigate to schema using valuePath
  if (config.valuePath) {
    const navigatedData = getValueByPath(data, config.valuePath);
    // Only use navigated data if it exists
    if (navigatedData !== null && navigatedData !== undefined) {
      data = navigatedData;
    }
  }

  // Validate it looks like a schema
  if (!data || typeof data !== 'object') {
    console.warn('[parseResponseToSchema] Data is not an object:', data);
    return null;
  }

  // Return as SchemaObject (may need further validation)
  return data as SchemaObject;
}

/**
 * Navigate `obj` using a swagger `value-path` (e.g. `Schema/Items`).
 *
 * Mirrors LogicAppsUX's `getObjectPropertyValue(response, extension['value-path'].split('/').filter(Boolean))`
 * (see libs/designer/src/lib/core/queries/connector.ts). The legacy
 * `x-ms-dynamic-schema` extension uses '/' as the path separator, not '.', and
 * lookups are case-insensitive (matches `getPropertyValue`'s default) — real
 * SharePoint responses often use casing that drifts from the swagger declaration
 * (e.g. `schema/items` vs declared `Schema/Items`).
 */
function getValueByPath(obj: unknown, path: string | undefined): unknown {
  if (!path || obj === null || obj === undefined) {
    return obj;
  }

  const parts = path.split('/').filter((s) => s.length > 0);
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    const record = current as Record<string, unknown>;
    if (part in record) {
      current = record[part];
      continue;
    }
    // Case-insensitive fallback
    const lower = part.toLowerCase();
    const match = Object.keys(record).find((k) => k.toLowerCase() === lower);
    current = match === undefined ? undefined : record[match];
  }

  return current;
}

// =============================================================================
// SCHEMA TO PARAMETERS CONVERSION
// =============================================================================

/**
 * Properties to hide from the dynamic-schema-generated input form. Mirrors
 * LogicAppsUX's input-schema filtering (`_isInternalParameter` +
 * `_isReadOnlyParameter`): hides `x-ms-visibility: internal`, `readOnly: true`,
 * and `x-ms-permission: "read-only"`. SharePoint's GetTable response marks
 * system columns (ID, Created, Modified, Editor, Author, Attachments,
 * ContentApprovalStatus, OData_*, etc.) read-only so the editor only renders
 * the user's custom columns.
 */
function shouldSkipDynamicProperty(propSchema: SchemaObject): boolean {
  const ext = propSchema as Record<string, unknown>;
  if (ext['x-ms-visibility'] === 'internal') return true;
  if (ext['readOnly'] === true) return true;
  const permission = ext['x-ms-permission'];
  if (typeof permission === 'string' && permission.toLowerCase() === 'read-only') return true;
  return false;
}

/**
 * Convert a dynamic schema to an array of ParameterInfo objects
 *
 * @param schema - The schema returned from the dynamic schema API
 * @param parentParameterName - Name of the parameter that triggered schema generation
 * @param nodeId - Optional node ID for generating unique parameter IDs
 * @param bodyKeyPrefix - Optional dotted body sub-path under which the resolved
 *   children serialize (e.g. `'parameters'` for SQL ExecuteProcedure_V2). Empty
 *   or undefined means the parent is the body root (e.g. SharePoint PostItem)
 *   and children serialize directly under `body`. Mirrors LogicAppsUX's
 *   `keyPrefix` (parent's `body.$...`).
 * @returns Array of generated ParameterInfo objects
 */
export function schemaToParameters(
  schema: SchemaObject,
  parentParameterName: string,
  nodeId?: string,
  bodyKeyPrefix?: string
): ParameterInfo[] {
  const parameters: ParameterInfo[] = [];

  if (!schema.properties) {
    return parameters;
  }

  // Handle required being either string[] or boolean
  const requiredProps: string[] = Array.isArray(schema.required) ? schema.required : [];

  for (const [propName, propSchema] of Object.entries(schema.properties)) {
    if (shouldSkipDynamicProperty(propSchema)) {
      continue;
    }

    // Check if this is a nested object with properties - if so, flatten it
    // This handles cases like "recipient" which contains "groupId" and "channelId"
    if (propSchema.type === 'object' && propSchema.properties) {
      const nestedParams = flattenNestedObjectProperty(
        propName,
        propSchema,
        requiredProps.includes(propName),
        parentParameterName,
        nodeId,
        bodyKeyPrefix
      );
      parameters.push(...nestedParams);
      continue;
    }

    const paramInfo = propertyToParameterInfo(
      propName,
      propSchema,
      requiredProps.includes(propName),
      parentParameterName,
      nodeId,
      bodyKeyPrefix
    );

    if (paramInfo) {
      parameters.push(paramInfo);
    }
  }

  return parameters;
}

/**
 * Flatten a nested object property into individual parameter fields
 *
 * For schemas like { recipient: { type: 'object', properties: { groupId: {...}, channelId: {...} } } }
 * This creates separate parameters for each nested property.
 */
function flattenNestedObjectProperty(
  parentPropName: string,
  propSchema: SchemaObject,
  parentRequired: boolean,
  parentParameterName: string,
  nodeId?: string,
  bodyKeyPrefix?: string
): ParameterInfo[] {
  const nestedParams: ParameterInfo[] = [];

  if (!propSchema.properties) {
    return nestedParams;
  }

  // Get required properties of the nested object
  const nestedRequired: string[] = Array.isArray(propSchema.required) ? propSchema.required : [];

  for (const [nestedName, nestedSchema] of Object.entries(propSchema.properties)) {
    if (shouldSkipDynamicProperty(nestedSchema)) {
      continue;
    }

    // Create a parameter key that reflects the nested path (e.g., "recipient.groupId")
    const paramKey = `${parentPropName}.${nestedName}`;

    // Nested property is required if parent is required AND property is in nested required list
    const isRequired = parentRequired && nestedRequired.includes(nestedName);

    const paramInfo = propertyToParameterInfo(
      paramKey,
      nestedSchema,
      isRequired,
      parentParameterName,
      nodeId,
      bodyKeyPrefix
    );

    if (paramInfo) {
      // Keep the nested structure info for proper serialization later
      paramInfo.info.nestedPath = paramKey;
      nestedParams.push(paramInfo);
    }
  }

  return nestedParams;
}

/**
 * Convert a single schema property to ParameterInfo
 */
function propertyToParameterInfo(
  name: string,
  propSchema: SchemaObject,
  required: boolean,
  parentParameterName: string,
  nodeId?: string,
  bodyKeyPrefix?: string
): ParameterInfo | null {
  // Type assertion for accessing extension properties
  const schemaWithExtensions = propSchema as Record<string, unknown>;

  const paramType = schemaTypeToParameterType(propSchema.type);

  // Determine visibility:
  // 1. Use explicit x-ms-visibility if present
  // 2. For non-required parameters, default to Advanced (hidden under "Advanced Options")
  // 3. Required parameters default to Default visibility
  const explicitVisibility = schemaWithExtensions['x-ms-visibility'] as string | undefined;
  let visibility: ParameterVisibility;
  if (explicitVisibility) {
    visibility = schemaVisibilityToParameterVisibility(explicitVisibility);
  } else if (!required) {
    // Non-required dynamic schema parameters go under Advanced Options
    visibility = ParameterVisibility.Advanced;
  } else {
    visibility = ParameterVisibility.Default;
  }

  // Check for dynamic values extensions (makes this a dropdown)
  const dynamicValues = schemaWithExtensions['x-ms-dynamic-values'] as Record<string, unknown> | undefined;
  const dynamicList = schemaWithExtensions['x-ms-dynamic-list'] as Record<string, unknown> | undefined;
  const hasDynamicDropdown = !!dynamicValues || !!dynamicList;

  // Determine editor type - prefer DynamicCombobox if dynamic values present
  let editor = inferEditorFromSchema(propSchema, paramType);
  if (hasDynamicDropdown) {
    editor = ParameterEditor.DynamicCombobox;
  }

  const prefix = nodeId ? `${nodeId}_` : '';
  const paramId = `${prefix}dyn_${parentParameterName}_${name}_${Date.now()}`;

  // Determine parameter location from x-ms-serialization extension
  // If serialization.property.type is 'pathtemplate', the parameter goes in the path
  // Otherwise, default to 'body' for dynamic schema parameters
  const rawSerialization = schemaWithExtensions['x-ms-serialization'] as
    | { property?: { type?: string; name?: string; parameterReference?: string }; skip?: boolean }
    | undefined;
  let parameterLocation: 'path' | 'query' | 'body' = 'body';

  if (rawSerialization?.property?.type === 'pathtemplate') {
    parameterLocation = 'path';
  }

  // Extract encoding level from x-ms-url-encoding extension
  // This determines how path parameters are URL-encoded
  const urlEncoding = schemaWithExtensions['x-ms-url-encoding'] as string | undefined;

  // Body-located children prefix their parameterKey with the parent's body
  // sub-path so the serializer reconstructs e.g. `body.parameters.Id` from
  // a parameterKey of `parameters.Id`. Path-located children (pathtemplate
  // serialization) keep their bare names because they don't live under body.
  const parameterKey =
    parameterLocation === 'body' && bodyKeyPrefix
      ? `${bodyKeyPrefix}.${name}`
      : name;

  // Build the parameter info
  const paramInfo: ParameterInfo = {
    id: paramId,
    parameterKey,
    parameterName: name,
    label: propSchema.title ?? formatLabel(name),
    type: paramType,
    required,
    editor,
    visibility,
    schema: propSchema,
    value: [],
    info: {
      in: parameterLocation,
      isGeneratedFromSchema: true,
      generatedByParameter: parentParameterName,
      ...(urlEncoding && { encode: urlEncoding }),
    },
    dynamicData: {
      status: DynamicLoadStatus.NotStarted,
    },
    showTokens: true,
    showErrors: true,
    ...(propSchema.description ? { placeholder: propSchema.description } : {}),
  };

  // If this property has dynamic values, parse and attach the config
  if (dynamicValues) {
    // Legacy x-ms-dynamic-values format
    const dvConfig = parseDynamicValuesFromProperty(dynamicValues, true);
    if (dvConfig) {
      paramInfo.info.dynamicValues = dvConfig;
    }
  } else if (dynamicList) {
    // Modern x-ms-dynamic-list format
    const dlConfig = parseDynamicValuesFromProperty(dynamicList, false);
    if (dlConfig) {
      paramInfo.info.dynamicValues = dlConfig;
    }
  }

  return paramInfo;
}

/**
 * Parse dynamic values config from a schema property extension
 *
 * Handles multiple swagger formats for parameter references:
 * - Modern: { "parameterReference": "groupId" }
 * - Legacy swagger: { "parameter": "groupId" }
 * - Plain string: "groupId" (treated as parameter reference for legacy extensions)
 * - Static value: { "value": someValue }
 */
function parseDynamicValuesFromProperty(
  extension: Record<string, unknown>,
  isLegacy: boolean
): DynamicValuesConfig | undefined {
  const operationId = extension['operationId'] as string | undefined;
  if (!operationId) {
    console.warn('[parseDynamicValuesFromProperty] Missing operationId');
    return undefined;
  }

  const config: DynamicValuesConfig = {
    operationId,
    isLegacy,
  };

  // Parse parameters - convert from raw extension format to DynamicValueParameter format
  const rawParams = extension['parameters'] as Record<string, unknown> | undefined;
  if (rawParams) {
    config.parameters = {};
    for (const [key, value] of Object.entries(rawParams)) {
      if (typeof value === 'string') {
        // Plain string is treated as parameter reference (legacy format)
        config.parameters[key] = { parameterReference: value };
      } else if (typeof value === 'object' && value !== null) {
        const objValue = value as Record<string, unknown>;

        // Check for various reference key names used in swagger extensions
        if ('parameterReference' in objValue) {
          // Modern format: { parameterReference: "..." }
          config.parameters[key] = { parameterReference: objValue.parameterReference as string };
        } else if ('parameter' in objValue) {
          // Legacy swagger format: { parameter: "..." }
          config.parameters[key] = { parameterReference: objValue.parameter as string };
        } else if ('value' in objValue) {
          // Static value format: { value: ... }
          config.parameters[key] = { value: objValue.value };
        } else {
          // Unknown object format - treat entire object as static value
          console.warn(`[parseDynamicValuesFromProperty] Unknown parameter format for "${key}":`, objValue);
          config.parameters[key] = { value };
        }
      } else {
        // Other primitives (numbers, booleans, null) - wrap as static value
        config.parameters[key] = { value };
      }
    }
  }

  // Parse value/title paths - different names in legacy vs modern
  // Only assign when value is defined (strict mode)
  if (isLegacy) {
    // x-ms-dynamic-values uses 'value-path', 'value-title', 'value-collection'
    const valuePath = extension['value-path'] as string | undefined;
    const titlePath = extension['value-title'] as string | undefined;
    const collectionPath = extension['value-collection'] as string | undefined;
    if (valuePath) config.itemValuePath = valuePath;
    if (titlePath) config.itemTitlePath = titlePath;
    if (collectionPath) config.itemsPath = collectionPath;
  } else {
    // x-ms-dynamic-list uses 'itemValuePath', 'itemTitlePath', 'itemsPath'
    const valuePath = extension['itemValuePath'] as string | undefined;
    const titlePath = extension['itemTitlePath'] as string | undefined;
    const itemsPath = extension['itemsPath'] as string | undefined;
    if (valuePath) config.itemValuePath = valuePath;
    if (titlePath) config.itemTitlePath = titlePath;
    if (itemsPath) config.itemsPath = itemsPath;
  }

  return config;
}

/**
 * Convert schema visibility to ParameterVisibility
 */
function schemaVisibilityToParameterVisibility(
  visibility?: string
): ParameterVisibility {
  switch (visibility) {
    case 'internal':
      return ParameterVisibility.Internal;
    case 'advanced':
      return ParameterVisibility.Advanced;
    case 'important':
      return ParameterVisibility.Important;
    default:
      return ParameterVisibility.Default;
  }
}

/**
 * Infer the best editor for a schema property
 */
function inferEditorFromSchema(schema: SchemaObject, paramType: ParameterType): ParameterEditor {
  // Enum values -> combobox
  if (schema.enum && schema.enum.length > 0) {
    return ParameterEditor.Combobox;
  }

  if (schema.format === 'date-time') {
    return ParameterEditor.DateTime;
  }

  switch (paramType) {
    case ParameterType.Boolean:
      return ParameterEditor.Boolean;
    case ParameterType.Array:
      return ParameterEditor.Array;
    case ParameterType.Object:
      return ParameterEditor.Dictionary;
    default:
      return ParameterEditor.String;
  }
}

/**
 * Format a property name as a display label
 */
function formatLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Validate that an access token exists and has basic JWT structure.
 * This is a lightweight client-side check to fail fast before API calls.
 */
function isValidAccessToken(token: string | null | undefined): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  // Basic JWT structure check: three base64 segments separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  // Each part should be non-empty and contain only valid base64url characters
  const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every((part) => part.length > 0 && base64UrlRegex.test(part));
}

/**
 * Build cache key for dynamic schema results.
 * Uses deterministic hashing to prevent cache poisoning from maliciously
 * crafted parameter values. MI routing fields are included so a
 * re-classification (e.g. once `useManagedConnectionInfo` settles) produces
 * a cache miss instead of returning a stale bare-route response.
 */
function buildCacheKey(
  connectionId: string,
  operationId: string,
  parameters: Record<string, unknown>,
  miRouting: { isManagedIdentity?: boolean | undefined; miAudience?: string | undefined; connectionRuntimeUrl?: string | undefined }
): string {
  const normalizedParams = normalizeForCacheKey(parameters);
  const mi = `${miRouting.isManagedIdentity ?? false}::${miRouting.miAudience ?? ''}::${miRouting.connectionRuntimeUrl ?? ''}`;
  return `schema::${connectionId}::${operationId}::${simpleHash(normalizedParams)}::${mi}`;
}

/**
 * Normalize an object for cache key generation.
 * Sorts keys recursively to ensure deterministic output.
 */
function normalizeForCacheKey(obj: unknown): string {
  if (obj === null || obj === undefined) {
    return 'null';
  }
  if (typeof obj !== 'object') {
    return String(obj);
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map(normalizeForCacheKey).join(',') + ']';
  }
  // Sort keys for deterministic ordering
  const sortedKeys = Object.keys(obj as Record<string, unknown>).sort();
  const pairs = sortedKeys.map((key) => {
    const value = (obj as Record<string, unknown>)[key];
    return `${key}:${normalizeForCacheKey(value)}`;
  });
  return '{' + pairs.join(',') + '}';
}

/**
 * Simple hash function for cache keys.
 * Uses djb2 algorithm - fast and sufficient for cache key deduplication.
 */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer and then to hex
  return (hash >>> 0).toString(16);
}

/**
 * Clear the dynamic schema cache
 */
export function clearDynamicSchemaCache(): void {
  dynamicSchemaCache.clear();
}

/**
 * Clear cache entries for a specific connection
 */
export function clearSchemaCacheForConnection(connectionId: string): void {
  for (const key of dynamicSchemaCache.keys()) {
    if (key.includes(connectionId)) {
      dynamicSchemaCache.delete(key);
    }
  }
}

// =============================================================================
// DEPENDENCY RESOLUTION
// =============================================================================

/**
 * Look up parameter value with case-insensitive fallback and swagger-derived mappings.
 *
 * Handles common mismatches between swagger parameter references and Otto's
 * internal parameter key naming:
 * - Case differences: "GroupId" vs "groupId"
 * - Swagger x-ms-summary mappings: "PostAs" (from "Post as") -> "poster" (param.name)
 *
 * This mirrors the lookupParameterValue() function in connectorDynamicService.ts
 * to ensure consistent parameter resolution across dynamic data operations.
 *
 * @param parameterValues - Current parameter values keyed by parameter name
 * @param reference - The parameter reference to look up (e.g., "PostAs")
 * @param swaggerMapping - Optional mapping from swagger x-ms-summary variations to canonical names
 */
function lookupParameterValue(
  parameterValues: Record<string, unknown>,
  reference: string,
  swaggerMapping?: Map<string, string>
): unknown {
  // Direct lookup (fastest path)
  if (reference in parameterValues) {
    return parameterValues[reference];
  }

  // Case-insensitive lookup
  const lowerRef = reference.toLowerCase();
  for (const [key, value] of Object.entries(parameterValues)) {
    if (key.toLowerCase() === lowerRef) {
      return value;
    }
  }

  // Use swagger-derived mapping to resolve x-ms-summary variations
  // e.g., "PostAs" (from x-ms-summary "Post as") → "poster" (canonical param.name)
  if (swaggerMapping) {
    const mappedName = swaggerMapping.get(lowerRef);
    if (mappedName) {
      // Try direct lookup with mapped name
      if (mappedName in parameterValues) {
        return parameterValues[mappedName];
      }
      // Try case-insensitive lookup with mapped name
      const lowerMapped = mappedName.toLowerCase();
      for (const [key, value] of Object.entries(parameterValues)) {
        if (key.toLowerCase() === lowerMapped) {
          return value;
        }
      }
    }
  }

  // Nested path matching: If reference is "groupId", try "recipient.groupId"
  for (const [key, value] of Object.entries(parameterValues)) {
    const keyLower = key.toLowerCase();
    if (keyLower.endsWith('.' + lowerRef)) {
      return value;
    }
    const lastSegment = key.split('.').pop()?.toLowerCase();
    if (lastSegment === lowerRef) {
      return value;
    }
  }

  // Body-prefix strip. Otto's swagger parser names body params without the
  // `body.` prefix (`recipient.groupId`), but x-ms-dynamic-schema configs and
  // in-editor writes commonly reference them as `body.recipient.groupId`.
  // Strip the prefix and retry the direct + case-insensitive match.
  if (lowerRef.startsWith('body.')) {
    return lookupParameterValue(parameterValues, reference.slice('body.'.length), swaggerMapping);
  }

  return undefined;
}

/**
 * Resolve parameter dependencies from current parameter values
 *
 * @param config - Dynamic schema configuration
 * @param parameterValues - Current values of all parameters keyed by parameter name
 * @param swaggerMapping - Optional mapping from swagger x-ms-summary variations to canonical names
 * @param optionalReferences - Lower-cased set of `parameterReference` names that are optional on the
 *   current operation. Missing values for these are silently dropped instead of
 *   blocking the fetch, mirroring LogicAppsUX's `parameterValidForDynamicCall`
 *   (`!parameter.required || parameterHasValue(parameter)`).
 * @returns Resolved parameters ready for API call, or null if a required dependency is unmet
 */
export function resolveDynamicSchemaParameters(
  config: DynamicSchemaConfig,
  parameterValues: Record<string, unknown>,
  swaggerMapping?: Map<string, string>,
  optionalReferences?: Set<string>
): Record<string, unknown> | null {
  if (!config.parameters) {
    return {};
  }

  const resolved: Record<string, unknown> = {};
  const missing: string[] = [];

  for (const [key, param] of Object.entries(config.parameters)) {
    if (param.value !== undefined) {
      // Static value
      resolved[key] = param.value;
    } else if (param.parameterReference) {
      // Reference to another parameter - use swagger mapping + fuzzy lookup
      const refValue = lookupParameterValue(parameterValues, param.parameterReference, swaggerMapping);
      if (refValue === undefined || refValue === null || refValue === '') {
        if (optionalReferences?.has(param.parameterReference.toLowerCase())) {
          // Optional dep with no value: omit silently, don't block.
          continue;
        }
        missing.push(param.parameterReference);
      } else {
        resolved[key] = refValue;
      }
    }
  }

  if (missing.length > 0) {
    console.debug('Missing required dependencies for dynamic schema:', missing);
    return null;
  }

  return resolved;
}

/**
 * Get list of parameter names that this dynamic schema depends on
 */
export function getSchemaDependencyParameterNames(config: DynamicSchemaConfig): string[] {
  if (!config.parameters) {
    return [];
  }

  const deps: string[] = [];
  for (const param of Object.values(config.parameters)) {
    if (param.parameterReference) {
      deps.push(param.parameterReference);
    }
  }

  return deps;
}

/**
 * Check if all dependencies are satisfied for dynamic schema
 */
export function areSchemaDependenciesSatisfied(
  config: DynamicSchemaConfig,
  parameterValues: Record<string, unknown>
): boolean {
  return resolveDynamicSchemaParameters(config, parameterValues) !== null;
}
